import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calculator,
  Loader2,
  AlertCircle,
  RefreshCw,
  CheckCircle,
  Phone,
  CalendarCheck,
} from 'lucide-react';
import { COMPANY_INFO } from '@/lib/constants';
import SEO from '@/components/SEO';
import QuestionnaireSteps from '@/components/estimation/QuestionnaireSteps';
import ReviewSuggestions from '@/components/estimation/ReviewSuggestions';
import EstimationResult from '@/components/estimation/EstimationResult';
import Button from '@/components/ui/Button';
import type {
  AnalyzeResponse,
  EstimateResponse,
  EstimateRequest,
  ProjectInputs,
  EstimationScreen,
} from '@/types/estimation';
import {
  analyzeProject,
  submitEstimate,
  healthCheck,
} from '@/service/estimationService';

// ─── Stepper config ────────────────────────────────────────────────

const STEPS = [
  { label: 'Votre projet', screens: ['details'] },
  { label: 'Analyse & Revue', screens: ['analyzing', 'review'] },
  { label: 'Résultat', screens: ['estimating', 'results'] },
];

function screenToStepIndex(screen: EstimationScreen): number {
  for (let i = 0; i < STEPS.length; i++) {
    if (STEPS[i].screens.includes(screen)) return i;
  }
  return 0;
}

// ─── Page Component ────────────────────────────────────────────────

const EstimationPage: React.FC = () => {
  // ── State ──
  const [screen, setScreen] = useState<EstimationScreen>('details');
  const [serverAvailable, setServerAvailable] = useState(true);

  const [inputs, setInputs] = useState<ProjectInputs>({
    projectDescription: '',
    platforms: [],
    scale: null,
    revenueModels: [],
  });

  const [analysis, setAnalysis] = useState<AnalyzeResponse | null>(null);
  const [estimate, setEstimate] = useState<EstimateResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const currentStepIndex = screenToStepIndex(screen);
  const showHero = screen === 'details';
  const showStepper = screen !== 'results';

  // ── Health check on mount ──

  useEffect(() => {
    const init = async () => {
      try {
        const isHealthy = await healthCheck();
        setServerAvailable(isHealthy);
      } catch {
        setServerAvailable(false);
      }
    };
    init();
  }, []);

  // ── Navigation handlers ──

  /** User submitted project details → run analysis (Step 1) */
  const handleInputsComplete = async (projectInputs: ProjectInputs) => {
    setInputs(projectInputs);
    setScreen('analyzing');
    setError(null);

    try {
      const response = await analyzeProject({
        project_description: projectInputs.projectDescription,
        template_id: null,
        platforms: projectInputs.platforms,
        scale: projectInputs.scale,
        revenue_models: projectInputs.revenueModels,
      });
      setAnalysis(response);
      setScreen('review');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      console.error('Analysis failed:', err);
      const message =
        err instanceof Error ? err.message : "L'analyse a échoué. Veuillez réessayer.";

      // 422 with invalid_project_description → go back to details form with inline error
      if (err?.status === 422 && err?.code === 'invalid_project_description') {
        setError(message);
        setScreen('details');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      setError(message);
      setScreen('error');
    }
  };

  /** User reviewed suggestions and submitted → calculate estimate (Step 2) */
  const handleReviewComplete = async (request: EstimateRequest) => {
    setScreen('estimating');
    setError(null);

    try {
      const response = await submitEstimate(request);
      setEstimate(response);
      setScreen('results');
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    } catch (err) {
      console.error('Estimation failed:', err);
      setError(
        err instanceof Error ? err.message : "Le calcul a échoué. Veuillez réessayer."
      );
      setScreen('error');
    }
  };

  /** Full reset */
  const handleReset = () => {
    setScreen('details');
    setInputs({
      projectDescription: '',
      platforms: [],
      scale: null,
      revenueModels: [],
    });
    setAnalysis(null);
    setEstimate(null);
    setError(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  /** Go back from review to details */
  const handleBackToDetails = () => {
    setScreen('details');
  };

  /** Go back from results to review */
  const handleModify = () => {
    setScreen('review');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  /** Retry after error */
  const handleRetry = () => {
    if (analysis) {
      setScreen('review');
    } else {
      setScreen('details');
    }
    setError(null);
  };

  // ── Render ──

  return (
    <>
      <SEO
        title="Estimation de projet personnalisée"
        description="Obtenez une estimation détaillée et personnalisée pour votre projet numérique."
        keywords="estimation projet, devis personnalisé, calcul prix développement, estimation application, devis web mobile"
        url="https://innov.orcsys.fr/estimation"
        type="website"
      />

      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-orcsys-50">
        {/* ═══════════════ Hero ═══════════════ */}
        {showHero && (
          <section className="relative pt-32 pb-12 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-white to-slate-50" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_20%,rgba(142,227,205,0.3)_0%,rgba(255,255,255,0)_60%)]" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_80%,rgba(155,135,245,0.2)_0%,rgba(255,255,255,0)_60%)]" />
            <div
              className="absolute inset-0 opacity-[0.03]"
              style={{
                backgroundImage: 'radial-gradient(#2b825b 1px, transparent 1px)',
                backgroundSize: '20px 20px',
              }}
            />

            <div className="container px-4 mx-auto relative z-10">
              <div className="max-w-4xl mx-auto text-center">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-orcsys-100 mb-6">
                    <Calculator className="w-10 h-10 text-orcsys-500" />
                  </div>

                  <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight">
                    Estimation{' '}
                    <span className="text-orcsys-500">personnalisée</span> de votre projet
                  </h1>

                  <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
                    Décrivez votre projet et obtenez une estimation détaillée avec
                    fonctionnalités, planning et prix indicatif.
                  </p>

                </motion.div>
              </div>
            </div>
          </section>
        )}

        {/* ═══════════════ Stepper ═══════════════ */}
        {showStepper && (
          <div className={`container px-4 mx-auto pb-2 ${showHero ? 'pt-4' : 'pt-28'}`}>
            <div className="max-w-3xl mx-auto">
              {/* Desktop */}
              <div className="hidden md:flex items-center justify-between mb-2">
                {STEPS.map((step, i) => (
                  <div key={i} className="flex items-center flex-1">
                    <div className="flex flex-col items-center flex-1">
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                          currentStepIndex >= i
                            ? 'bg-orcsys-500 text-white'
                            : 'bg-slate-200 text-slate-500'
                        }`}
                      >
                        {currentStepIndex > i ? (
                          <CheckCircle className="w-6 h-6" />
                        ) : (
                          i + 1
                        )}
                      </div>
                      <p
                        className={`text-xs mt-2 text-center ${
                          currentStepIndex >= i
                            ? 'text-slate-900 font-medium'
                            : 'text-slate-400'
                        }`}
                      >
                        {step.label}
                      </p>
                    </div>
                    {i < STEPS.length - 1 && (
                      <div
                        className={`h-1 flex-1 mx-2 transition-all ${
                          currentStepIndex > i ? 'bg-orcsys-500' : 'bg-slate-200'
                        }`}
                      />
                    )}
                  </div>
                ))}
              </div>

              {/* Mobile */}
              <div className="md:hidden">
                <div className="flex items-center justify-between mb-3">
                  {STEPS.map((_, i) => (
                    <div key={i} className="flex items-center flex-1">
                      <div className="flex flex-col items-center flex-1">
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm ${
                            currentStepIndex >= i
                              ? 'bg-orcsys-500 text-white'
                              : 'bg-slate-200 text-slate-500'
                          }`}
                        >
                          {currentStepIndex > i ? (
                            <CheckCircle className="w-4 h-4" />
                          ) : (
                            i + 1
                          )}
                        </div>
                      </div>
                      {i < STEPS.length - 1 && (
                        <div
                          className={`h-0.5 flex-1 mx-1 ${
                            currentStepIndex > i ? 'bg-orcsys-500' : 'bg-slate-200'
                          }`}
                        />
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-center text-sm font-semibold text-slate-900">
                  Étape {currentStepIndex + 1}/{STEPS.length} :{' '}
                  {STEPS[currentStepIndex]?.label}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ═══════════════ Content ═══════════════ */}
        <section className={`py-8 pb-20 ${!showHero && !showStepper ? 'pt-32' : ''}`}>
          <div className="container px-4 mx-auto">
            <AnimatePresence mode="wait">
              {/* ── Questionnaire (details only, no templates) ── */}
              {screen === 'details' && (
                <motion.div
                  key="questionnaire"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <QuestionnaireSteps
                    serverAvailable={serverAvailable}
                    initialInputs={inputs}
                    apiError={screen === 'details' ? error : null}
                    onClearError={() => setError(null)}
                    onComplete={handleInputsComplete}
                  />
                </motion.div>
              )}

              {/* ── Analysis Loading ── */}
              {screen === 'analyzing' && (
                <motion.div
                  key="analyzing"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="max-w-2xl mx-auto mt-12 px-2 sm:px-0"
                >
                  <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-12 text-center">
                    <div className="relative inline-block mb-6">
                      <Loader2 className="w-16 h-16 text-orcsys-500 animate-spin" />
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-3">
                      Analyse de votre projet en cours...
                    </h3>
                    <p className="text-sm sm:text-base text-slate-600 mb-2">
                      Identification des fonctionnalités, estimation des heures,
                    </p>
                    <p className="text-sm sm:text-base text-slate-600">sélection des technologies...</p>

                    <div className="mt-8 w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                      <motion.div
                        className="h-full bg-gradient-to-r from-orcsys-400 to-orcsys-600 rounded-full"
                        initial={{ width: '0%' }}
                        animate={{ width: '90%' }}
                        transition={{ duration: 12, ease: 'easeOut' }}
                      />
                    </div>
                    <p className="text-xs text-slate-400 mt-3">
                      Cela peut prendre quelques secondes
                    </p>
                  </div>
                </motion.div>
              )}

              {/* ── Review Suggestions ── */}
              {screen === 'review' && analysis && (
                <motion.div
                  key="review"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <ReviewSuggestions
                    analysis={analysis}
                    inputs={inputs}
                    onSubmit={handleReviewComplete}
                    onBack={handleBackToDetails}
                  />
                </motion.div>
              )}

              {/* ── Estimate Loading ── */}
              {screen === 'estimating' && (
                <motion.div
                  key="estimating"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="max-w-2xl mx-auto px-2 sm:px-0"
                >
                  <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-12 text-center">
                    <Loader2 className="w-12 h-12 sm:w-16 sm:h-16 text-orcsys-500 animate-spin mx-auto mb-6" />
                    <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-3">
                      Calcul de l'estimation...
                    </h3>
                    <p className="text-sm sm:text-base text-slate-600">
                      Compilation des résultats et génération du planning
                    </p>
                  </div>
                </motion.div>
              )}

              {/* ── Results ── */}
              {screen === 'results' && estimate && (
                <motion.div
                  key="results"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <EstimationResult
                    estimate={estimate}
                    analysis={analysis}
                    inputs={inputs}
                    onReset={handleReset}
                    onModify={handleModify}
                  />
                </motion.div>
              )}

              {/* ── Error ── */}
              {screen === 'error' && (
                <motion.div
                  key="error"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="max-w-2xl mx-auto px-2 sm:px-0"
                >
                  <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-12 text-center">
                    <AlertCircle className="w-12 h-12 sm:w-16 sm:h-16 text-red-400 mx-auto mb-6" />
                    <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-3">
                      Une erreur est survenue
                    </h3>
                    <p className="text-sm sm:text-base text-slate-600 mb-8">
                      {error || 'Veuillez réessayer.'}
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                      <Button
                        onClick={handleRetry}
                        className="bg-orcsys-500 hover:bg-orcsys-600"
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Réessayer
                      </Button>
                      <Button variant="outline" onClick={handleReset}>
                        Recommencer
                      </Button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </section>

        {/* ═══════════════ CTA Section ═══════════════ */}
        {screen === 'results' && (
          <section className="py-12 sm:py-20 bg-gradient-to-br from-orcsys-500 to-orcsys-600">
            <div className="container px-4 mx-auto text-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="max-w-3xl mx-auto"
              >
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4">
                  Prêt à concrétiser votre projet ?
                </h2>
                <p className="text-base sm:text-lg text-white/90 mb-8">
                  Cette estimation est indicative. Contactez-nous pour obtenir un devis
                  personnalisé et détaillé. Nous vous répondrons sous 24h ouvrées.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button.Link
                    href={`tel:${COMPANY_INFO.phone}`}
                    className="bg-white text-orcsys-600 hover:bg-white/90 px-8 py-3 text-lg"
                  >
                    <Phone className="w-5 h-5 mr-2" />
                    Nous appeler
                  </Button.Link>
                  <Button.Link
                    href="/booking"
                    variant="outline"
                    className="border-white text-white hover:bg-white/10 px-8 py-3 text-lg"
                  >
                    <CalendarCheck className="w-5 h-5 mr-2" />
                    Prendre rendez-vous
                  </Button.Link>
                </div>
              </motion.div>
            </div>
          </section>
        )}
      </div>
    </>
  );
};

export default EstimationPage;
